import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getAppointmentSystemConnectionTypes } from '~/constants'
import type {
  ConnectionType,
  GETConnection,
  OrganizationSubscription,
  POSTConnection,
  TerminalName,
} from '~/services/apiClient'
import {
  ConnectionsApi,
  ConnectionValidationStatus,
} from '~/services/apiClient'
import type { ConnectionMetadata } from '~/models/connectionMetadata'
import { connectionMetadataFromGET } from '~/models/connectionMetadata'

export const useConnectionsStore = defineStore('connections', () => {
  const loadingPromise = ref<Promise<void> | null>(null)
  const connections = ref<GETConnection[]>([])
  const connectionLookup = ref(new Map<ConnectionType, GETConnection>())
  const loadedFromAPI = ref(false)
  const loadedSubscription = ref<OrganizationSubscription | undefined>()
  const loadingSubscription = ref<OrganizationSubscription | undefined>()
  // The org's editable connection registry, served by the backend and loaded
  // alongside the connections themselves. TMS integrations are filtered out on
  // load (see `load()`).
  const allEditableConnectionMetas = ref<ConnectionMetadata[]>([])
  const loading = computed(() => loadingPromise.value !== null)
  const validating = ref(false)

  const validatedConnections = computed(() => {
    return connections.value.filter(
      (c) => c.validation_status === ConnectionValidationStatus.Validated
    )
  })
  const disabledConnections = computed(() => {
    return connections.value.filter(
      (c) => c.validation_status === ConnectionValidationStatus.Disabled
    )
  })

  const notUsedConnections = computed(() => {
    return connections.value.filter(
      (c) => c.validation_status === ConnectionValidationStatus.NotUsed
    )
  })
  // Catalog field `setup_covered_by` names the connection that covers this row.
  // Read `connections` (replaced on every update) so this stays live after save.
  function isSetupCovered(type: ConnectionType): boolean {
    const coveredBy = allEditableConnectionMetas.value.find(
      (meta) => meta.type === type
    )?.setupCoveredBy
    if (!coveredBy) return false
    const covering = connections.value.find((c) => c.type === coveredBy)
    return covering?.validation_status === ConnectionValidationStatus.Validated
  }

  // Connections the user can set up and act on. Managed connections are surfaced
  // separately (`managedConnectionMetas`) so they stay out of the action-oriented
  // counts, banners, and "needs attention" section. The All tab still lists a
  // covered row; setup and Needs attention use `setupConnectionTypeMetas`.
  const editableConnectionTypeMetasForOrg = computed(() =>
    allEditableConnectionMetas.value.filter((m) => !m.managed)
  )

  const setupConnectionTypeMetas = computed(() =>
    editableConnectionTypeMetasForOrg.value.filter(
      (m) => !isSetupCovered(m.type)
    )
  )

  // Connections Dray Dog provisions on the user's behalf (e.g. LBCT API).
  // Rendered read-only.
  const managedConnectionMetas = computed(() =>
    allEditableConnectionMetas.value.filter((m) => m.managed)
  )

  const totalEditableConnectionTypes = computed(() => {
    return editableConnectionTypeMetasForOrg.value.length
  })

  const editableConnectionTypesForOrg = computed(() => {
    return new Set(editableConnectionTypeMetasForOrg.value.map((c) => c.type))
  })

  const remainingConnectionsCount = computed(() => {
    if (!loadedFromAPI.value) return 0

    const setupTypes = new Set(
      setupConnectionTypeMetas.value.map((m) => m.type)
    )
    const handledCount = [
      ...validatedConnections.value,
      ...notUsedConnections.value,
      ...disabledConnections.value,
    ].filter((c) => setupTypes.has(c.type)).length

    return Math.max(0, setupTypes.size - handledCount)
  })

  const shouldShowSetupModal = computed(() => {
    if (!loadedFromAPI.value) return false

    const hasValidCredentials = validatedConnections.value.length > 0
    const editableTypes = new Set(
      allEditableConnectionMetas.value.map((m) => m.type)
    )
    const hasProblematicConnections = connections.value.some((c) => {
      if (c.validation_status !== ConnectionValidationStatus.Invalid) {
        return false
      }
      // A saved row this org cannot edit is not a setup problem. That includes
      // FMS API for an org without it enabled, and TMS rows dropped at load.
      // Managed rows stay in this list, so a bad LBCT API still opens setup.
      if (!editableTypes.has(c.type)) {
        return false
      }
      // An invalid login is not a setup problem while its covering connection works.
      if (isSetupCovered(c.type)) {
        return false
      }
      return true
    })

    // Show modal if: no valid credentials OR has problems OR has remaining connections to handle
    return (
      !hasValidCredentials ||
      hasProblematicConnections ||
      remainingConnectionsCount.value > 0
    )
  })

  function load(subscription?: OrganizationSubscription): Promise<void> {
    const api = new ConnectionsApi()
    loadingSubscription.value = subscription
    loadingPromise.value = Promise.all([
      api.listConnectionsConnectionsGet(),
      api.listEditableConnectionTypesConnectionsEditableTypesGet(subscription),
    ])
      .then(([connectionsResp, editableTypesResp]) => {
        connections.value = connectionsResp.data
        connectionsResp.data.forEach((c) => {
          connectionLookup.value.set(c.type, c)
        })
        // The backend serves the per-org editable registry. Drop TMS
        // integrations — they have no bucket in the credentials UI and would
        // otherwise inflate the action counts / setup badge.
        allEditableConnectionMetas.value = editableTypesResp.data
          .map(connectionMetadataFromGET)
          .filter((m) => m.category !== 'tms')
        // Only mark as loaded on success — failed requests (e.g. 401 during
        // sign-up/logout flows) must not poison the cache and block future fetches.
        loadedFromAPI.value = true
        loadedSubscription.value = subscription
      })
      .finally(() => {
        loadingPromise.value = null
        loadingSubscription.value = undefined
      })
    return loadingPromise.value
  }

  async function loadConnectionsIfNotLoaded(
    subscription?: OrganizationSubscription
  ): Promise<void> {
    if (loadedFromAPI.value && loadedSubscription.value === subscription) {
      return
    }
    if (loadingPromise.value) {
      if (loadingSubscription.value === subscription) {
        await loadingPromise.value
        return
      }
      try {
        await loadingPromise.value
      } catch {
        // A failed request for another subscription scope must not prevent this
        // caller from loading the scope it requested.
      }
      await loadConnectionsIfNotLoaded(subscription)
      return
    }
    await load(subscription)
  }

  function saveAndValidate(connections: POSTConnection[]) {
    validating.value = true
    const api = new ConnectionsApi()
    return api
      .saveAndValidateConnectionsConnectionsSaveAndValidatePost(connections)
      .then((resp) => {
        _updateConnections(resp.data)
        return resp
      })
      .finally(() => {
        validating.value = false
      })
  }

  function update(connection: GETConnection) {
    const api = new ConnectionsApi()
    return api.updateConnectionConnectionsPut(connection)
  }

  async function markNotUsed(connectionType: ConnectionType) {
    const api = new ConnectionsApi()
    const resp =
      await api.markConnectionNotUsedConnectionsTypeMarkNotUsedPost(
        connectionType
      )
    _updateConnections([resp.data])
  }

  function unmarkNotUsed(connectionType: ConnectionType) {
    const connection = connectionLookup.value.get(connectionType)
    if (!connection) {
      return
    }

    // Update the connection status locally from NotUsed to Unvalidated
    // This preserves existing credentials while allowing the user to edit them
    // The backend will be updated when the user saves/validates the connection
    const updatedConnection: GETConnection = {
      ...connection,
      validation_status: ConnectionValidationStatus.Unvalidated,
    }
    _updateConnections([updatedConnection])
  }

  function _updateConnections(newConnections: GETConnection[]) {
    for (const connection of newConnections) {
      connectionLookup.value.set(connection.type, connection)
    }
    connections.value = [...connectionLookup.value.values()]
    loadedFromAPI.value = true
  }

  function hasValidatedConnectionForTerminal(terminal: TerminalName): boolean {
    const suitableConnectionTypes =
      getAppointmentSystemConnectionTypes(terminal)
    if (suitableConnectionTypes === null) return false

    return suitableConnectionTypes.some((type) => {
      const connection = connectionLookup.value.get(type)
      return (
        connection?.validation_status === ConnectionValidationStatus.Validated
      )
    })
  }

  function reset() {
    loadingPromise.value = null
    connections.value = []
    connectionLookup.value = new Map()
    allEditableConnectionMetas.value = []
    loadedFromAPI.value = false
    loadedSubscription.value = undefined
    loadingSubscription.value = undefined
    validating.value = false
  }

  return {
    loading,
    validating,
    loadedFromAPI,
    connections,
    connectionLookup,
    totalEditableConnectionTypes,
    remainingConnectionsCount,
    shouldShowSetupModal,
    validatedConnections,
    disabledConnections,
    notUsedConnections,
    editableConnectionTypeMetasForOrg,
    editableConnectionTypesForOrg,
    isSetupCovered,
    setupConnectionTypeMetas,
    managedConnectionMetas,
    load,
    loadConnectionsIfNotLoaded,
    saveAndValidate,
    update,
    markNotUsed,
    unmarkNotUsed,
    hasValidatedConnectionForTerminal,
    reset,
  }
})
